// FR-4 Score-follow page: import MusicXML, render, follow with strict/lenient modes,
// real-time pitch comparison, error notebook + report generation.

import type { Page } from '../page';
import { el, radarChart, scoreRing } from '../components';
import { state, toast } from '../store';
import { showMicError } from '../mic';
import { parseMusicXML } from '../../score/parser';
import { ScoreRenderer } from '../../score/renderer';
import { PlaybackEngine } from '../../accompaniment/playback';
import { generateAccompaniment } from '../../accompaniment/arpeggio';
import { midiToFreq, freqToCents, midiToName } from '../../audio/freq';
import { pitchScoreFromRMSE } from '../../assessment/score';
import { saveReport } from '../../storage/db';
import type { MusicXMLScore, NoteEvent, ErrorRecord, PracticeReport, PracticeMode } from '../../types';

const COPYRIGHT_KEY = 'violinlab.copyrightAccepted';
const PRESETS: Record<string, { cents: number; label: string }> = {
  pro: { cents: 5, label: '专业 ±5' },
  advanced: { cents: 10, label: '进阶 ±10' },
  beginner: { cents: 15, label: '初学 ±15' },
};

export function scorePage(): Page {
  let cleanup: (() => void) | null = null;
  return {
    mount(container) {
      let score: MusicXMLScore | null = null;
      let renderer: ScoreRenderer | null = null;
      let playback: PlaybackEngine | null = null;
      let timeline: { note: NoteEvent; measure: number }[] = [];
      let noteMap = new Map<string, { note: NoteEvent; measure: number }>();
      let mode: PracticeMode = state.prefs.pitchTolerancePreset === 'pro' ? 'strict' : 'lenient';
      let tolerance = state.prefs.pitchTolerance;
      let deviations: number[] = [];
      let errorIds = new Set<string>();
      let errorRecords: ErrorRecord[] = [];
      let coveredNotes = new Set<string>();
      let consecutiveFail = 0;
      let micUnsub: (() => void) | null = null;
      let arpOn = false;
      let metronomeOn = true;
      let startedAt = 0;
      let lastBeat = 0;

      const fileInput = el('input', { type: 'file', accept: '.musicxml,.xml,.mxl', style: 'display:none' });
      const importBtn = el('button', { class: 'primary', text: '导入 MusicXML' });
      const sampleBtn = el('button', { class: 'chip', text: '加载示例（小星星）' });
      const scoreArea = el('div', { class: 'card', style: 'overflow-x:auto' });
      const emptyState = el('div', { class: 'empty', text: '导入乐谱后开始跟弹练习' });
      scoreArea.append(emptyState);

      // Controls
      const playBtn = el('button', { class: 'primary', text: '▶ 播放', disabled: true });
      const stopBtn = el('button', { class: 'ghost', text: '⏹ 停止', disabled: true });
      const modeBtn = el('button', { class: 'chip active', text: '模式: 宽容' });
      const presetBtns = (Object.keys(PRESETS) as string[]).map((k) => {
        const b = el('button', { class: 'chip' + (k === 'beginner' ? ' active' : ''), text: PRESETS[k].label });
        b.onclick = () => {
          tolerance = PRESETS[k].cents;
          state.setPrefs({ pitchTolerance: tolerance, pitchTolerancePreset: k as 'pro' | 'advanced' | 'beginner' });
          presetBtns.forEach((x) => x.classList.remove('active'));
          b.classList.add('active');
        };
        return b;
      });
      const metBtn = el('button', { class: 'chip active', text: '🥁 节拍器' });
      metBtn.onclick = () => { metronomeOn = !metronomeOn; metBtn.classList.toggle('active', metronomeOn); playback?.setMetronome(metronomeOn); };
      const arpBtn = el('button', { class: 'chip', text: '🎹 琶音伴奏' });
      arpBtn.onclick = () => { arpOn = !arpOn; arpBtn.classList.toggle('active', arpOn); rebuildArp(); };

      const rateChips = [0.5, 0.75, 1, 1.25, 1.5].map((r) => {
        const b = el('button', { class: 'chip' + (r === 1 ? ' active' : ''), text: `${r}x` });
        b.onclick = () => { rateChips.forEach((x) => x.classList.remove('active')); b.classList.add('active'); playback?.setRate(r); };
        return b;
      });
      const bpmInput = el('input', { type: 'number', min: '40', max: '240', step: '1', value: '100', style: 'width:80px' });
      bpmInput.onchange = () => {
        const v = Math.max(40, Math.min(240, Number(bpmInput.value) || 100));
        bpmInput.value = String(v);
        if (score) score.bpm = v;
        restartPlaybackIfRunning();
      };

      const statsEl = el('div', { class: 'row wrap tiny muted' });
      const strictPanel = el('div', { class: 'card', style: 'display:none' });

      const controls = el('div', { class: 'card' }, [
        el('div', { class: 'row wrap between' }, [playBtn, stopBtn, modeBtn]),
        el('h2', { text: '音准容差' }),
        el('div', { class: 'row wrap' }, presetBtns),
        el('h2', { text: '速度倍率' }),
        el('div', { class: 'row wrap' }, rateChips),
        el('div', { class: 'row center', style: 'margin-top:8px' }, [el('span', { class: 'tiny muted', text: 'BPM' }), bpmInput]),
        el('div', { class: 'row wrap', style: 'margin-top:10px' }, [metBtn, arpBtn]),
        statsEl,
      ]);

      modeBtn.onclick = () => {
        mode = mode === 'strict' ? 'lenient' : 'strict';
        modeBtn.textContent = `模式: ${mode === 'strict' ? '严格' : '宽容'}`;
        modeBtn.classList.toggle('active', mode === 'lenient');
        consecutiveFail = 0;
      };

      importBtn.onclick = () => {
        if (!localStorage.getItem(COPYRIGHT_KEY)) {
          showCopyrightModal(() => fileInput.click());
        } else {
          fileInput.click();
        }
      };
      fileInput.onchange = async () => {
        const f = fileInput.files?.[0];
        if (!f) return;
        const text = await f.text();
        loadScore(text, f.name);
      };
      sampleBtn.onclick = async () => {
        const res = await fetch('/sample-twinkle.musicxml');
        const text = await res.text();
        loadScore(text, '小星星');
      };

      function loadScore(xml: string, name: string) {
        const result = parseMusicXML(xml);
        if (!result.success || !result.score) {
          toast('乐谱解析失败：' + (result.errors[0] ?? '未知错误'));
          return;
        }
        score = result.score;
        if (result.unsupportedElements.length) toast('部分特殊记号已隐藏');
        timeline = renderer === null ? [] : [];
        renderer = new ScoreRenderer(score);
        timeline = renderer.getTimeline();
        noteMap = new Map(timeline.map((t) => [t.note.id, t]));
        scoreArea.innerHTML = '';
        scoreArea.append(renderer.el);
        playBtn.disabled = false;
        bpmInput.value = String(score.bpm);
        const total = Math.max(...timeline.map((t) => t.note.startTime + t.note.duration), 1);
        statsEl.innerHTML = '';
        statsEl.append(el('span', { class: 'badge', text: `🎵 ${name}` }), el('span', { class: 'badge', text: `${score.bpm} BPM` }), el('span', { class: 'badge', text: `${timeline.length} 音` }), el('span', { class: 'badge', text: `${total} 拍` }));
        resetSession();
        toast('乐谱已加载');
      }

      function rebuildArp() {
        if (!score || !playback) return;
        if (arpOn && score.chords && score.chords.length) {
          playback.setArpeggio(generateAccompaniment({ tempo: score.bpm, key: 'C', timeSignature: score.timeSignature, chords: score.chords, pattern: 'up' }));
        } else if (arpOn) {
          playback.setArpeggio(generateAccompaniment({ tempo: score.bpm, key: 'C', timeSignature: score.timeSignature, chords: [], pattern: 'up' }));
        } else {
          playback.setArpeggio(null);
        }
      }

      function resetSession() {
        deviations = [];
        errorIds = new Set();
        errorRecords = [];
        coveredNotes = new Set();
        consecutiveFail = 0;
        renderer?.markErrors(new Set());
        renderer?.setHighlight(new Set());
      }

      function restartPlaybackIfRunning() {
        if (playback?.isRunning) {
          playback.stop();
          startPlayback();
        }
      }

      async function startPlayback() {
        if (!score || !renderer) return;
        if (!state.engine.isRunning) {
          try { await state.engine.start(); } catch (e) { showMicError(e); return; }
        }
        const ctx = state.engine.audioContext;
        if (!ctx) return;
        const total = Math.max(...timeline.map((t) => t.note.startTime + t.note.duration), 1) + 2;
        playback = new PlaybackEngine(ctx, score.bpm, total, state.prefs.midiOffset, {
          onBeat: (beat) => {
            lastBeat = beat;
            const ids = new Set(renderer!.notesAtBeat(beat));
            renderer!.setHighlight(ids);
          },
          onEnd: finishSession,
        });
        playback.setMelody(timeline);
        playback.setMetronome(metronomeOn);
        rebuildArp();
        resetSession();
        startedAt = Date.now();
        playback.start();
        playBtn.textContent = '⏸ 暂停';
        stopBtn.disabled = false;
        if (!micUnsub) micUnsub = state.engine.onFrameEvent(onMicFrame);
      }

      function onMicFrame(frame: { freq: number; midi: number; confidence: number; time: number }) {
        if (!playback?.isRunning || !score) return;
        if (frame.confidence < 0.4 || frame.freq <= 0) return;
        const beat = playback.currentBeat();
        const activeIds = renderer!.notesAtBeat(beat);
        if (activeIds.length === 0) return;
        // Find the active note closest in pitch to the detected frequency.
        let best: { note: NoteEvent; measure: number } | null = null;
        let bestDist = Infinity;
        for (const id of activeIds) {
          const t = noteMap.get(id);
          if (!t) continue;
          const dist = Math.abs(freqToCents(frame.freq, midiToFreq(t.note.pitch, state.prefs.A4)));
          if (dist < bestDist) { bestDist = dist; best = t; }
        }
        if (!best) return;
        coveredNotes.add(best.note.id);
        const expectedFreq = midiToFreq(best.note.pitch, state.prefs.A4);
        const cents = 1200 * Math.log2(frame.freq / expectedFreq);
        deviations.push(cents);

        if (Math.abs(cents) > tolerance) {
          if (!errorIds.has(best.note.id)) {
            errorIds.add(best.note.id);
            errorRecords.push({ timestamp: frame.time, type: 'noteMistake', expectedPitch: best.note.pitch, actualPitch: Math.round(frame.midi), measure: best.measure, beat });
            renderer!.markErrors(errorIds);
          }
          consecutiveFail++;
          if (mode === 'strict') {
            if (Math.abs(cents) > 50 || consecutiveFail >= 3) pauseForRetry(best.measure);
          }
        } else {
          consecutiveFail = 0;
        }
      }

      function pauseForRetry(measure: number) {
        playback?.pause();
        playBtn.textContent = '▶ 继续';
        strictPanel.style.display = '';
        strictPanel.innerHTML = '';
        const retry = el('button', { class: 'primary', text: '🔄 重试当前小节' });
        const skip = el('button', { class: 'chip', text: '跳过本小节' });
        const cont = el('button', { class: 'chip', text: '继续练习' });
        retry.onclick = () => { strictPanel.style.display = 'none'; seekToMeasure(measure); playback?.start(playback.currentBeat()); playBtn.textContent = '⏸ 暂停'; };
        skip.onclick = () => { strictPanel.style.display = 'none'; seekToMeasure(measure + 1); playback?.start(playback.currentBeat()); playBtn.textContent = '⏸ 暂停'; };
        cont.onclick = () => { strictPanel.style.display = 'none'; consecutiveFail = 0; playback?.start(playback.currentBeat()); playBtn.textContent = '⏸ 暂停'; };
        strictPanel.append(el('p', { class: 'muted', text: `⚠️ 第 ${measure} 小节连续出错，请重试` }), el('div', { class: 'row wrap' }, [retry, skip, cont]));
      }

      function seekToMeasure(measure: number) {
        const beatsPerMeasure = score!.timeSignature[0];
        playback?.seek((measure - 1) * beatsPerMeasure);
      }

      function finishSession() {
        playBtn.textContent = '▶ 播放';
        stopBtn.disabled = true;
        micUnsub?.(); micUnsub = null;
        if (!score) return;
        const rmse = deviations.length ? Math.sqrt(deviations.reduce((a, b) => a + b * b, 0) / deviations.length) : 100;
        const pitchScore = pitchScoreFromRMSE(rmse);
        const coverage = timeline.length ? coveredNotes.size / timeline.length : 0;
        const rhythmScore = Math.round(coverage * 100);
        const overall = Math.round(0.6 * pitchScore + 0.4 * rhythmScore);
        const radar = { 音准: Math.round(pitchScore), 节奏: rhythmScore, 完成度: Math.round(coverage * 100) };
        const report: PracticeReport = {
          id: `r${Date.now()}`, user_id: 'local', score_id: score.title,
          started_at: startedAt, ended_at: Date.now(),
          overall_score: overall, pitch_score: Math.round(pitchScore), rhythm_score: rhythmScore,
          error_records: errorRecords, radar_data: radar,
        };
        saveReport(report).catch(() => undefined);
        showReport(report);
        // Lenient end review: keep errors highlighted (FR-4.7).
        renderer?.markErrors(errorIds);
        toast(`练习完成 · 总分 ${overall}`);
      }

      function showReport(report: PracticeReport) {
        controls.style.display = 'none';
        const radarCanvas = el('canvas');
        radarChart(radarCanvas, ['音准', '节奏', '完成度'], [report.radar_data['音准'] ?? 0, report.radar_data['节奏'] ?? 0, report.radar_data['完成度'] ?? 0]);
        const errList = report.error_records.slice(0, 8).map((e) =>
          el('div', { class: 'row between tiny' }, [
            el('span', { text: `第 ${e.measure ?? '?'} 小节 · ${e.type === 'noteMistake' ? '错音' : e.type}` }),
            el('span', { class: 'muted', text: e.expectedPitch != null ? `应 ${midiToName(e.expectedPitch)} → 实 ${e.actualPitch != null ? midiToName(e.actualPitch) : '?'}` : '' }),
          ]),
        );
        const again = el('button', { class: 'primary', text: '再练一次' });
        again.onclick = () => { controls.style.display = ''; reportCard.remove(); resetSession(); };
        const reportCard = el('div', { class: 'card' }, [
          el('h2', { text: '练习报告' }),
          el('div', { class: 'row center', style: 'gap:20px' }, [scoreRing(report.overall_score), el('div', {}, [radarCanvas])]),
          el('h2', { text: `错题 (${report.error_records.length})` }),
          ...errList,
          el('div', { class: 'row', style: 'margin-top:12px' }, [again]),
        ]);
        container.append(reportCard);
      }

      playBtn.onclick = () => {
        if (playback?.isRunning) { playback.pause(); playBtn.textContent = '▶ 继续'; return; }
        if (playback && !playback.isRunning && deviations.length === 0 && lastBeat === 0) { startPlayback(); return; }
        if (playback && !playback.isRunning) { playback.start(playback.currentBeat()); playBtn.textContent = '⏸ 暂停'; if (!micUnsub) micUnsub = state.engine.onFrameEvent(onMicFrame); return; }
        startPlayback();
      };
      stopBtn.onclick = () => { playback?.stop(); playBtn.textContent = '▶ 播放'; stopBtn.disabled = true; micUnsub?.(); micUnsub = null; resetSession(); renderer?.setHighlight(new Set()); };

      container.append(
        el('h1', { text: '乐谱跟弹' }),
        el('p', { class: 'sub', text: '导入 MusicXML，跟弹练习，实时纠错' }),
        el('div', { class: 'card' }, [el('div', { class: 'row wrap' }, [importBtn, sampleBtn]), fileInput]),
        scoreArea,
        controls,
        strictPanel,
        el('p', { class: 'tiny muted', text: '🔒 乐谱仅在本地解析，不上传。首次导入需确认版权合规。' }),
      );
      cleanup = () => { playback?.stop(); micUnsub?.(); micUnsub = null; if (state.engine.isRunning) state.engine.stop(); };
    },
    unmount() {
      cleanup?.();
      cleanup = null;
    },
  };
}

function showCopyrightModal(onAccept: () => void): void {
  const check = el('input', { type: 'checkbox' });
  const accept = el('button', { class: 'primary', text: '确认并导入', disabled: true });
  const cancel = el('button', { class: 'ghost', text: '取消' });
  check.onchange = () => { accept.disabled = !check.checked; };
  const modal = el('div', { class: 'modal' }, [
    el('h2', { text: '乐谱版权合规承诺书' }),
    el('p', { class: 'muted', text: '本应用仅提供乐谱渲染工具，不存储/不上传您的乐谱。请确认您导入的乐谱拥有合法使用权，版权责任由用户自行承担。' }),
    el('label', { class: 'row', style: 'gap:8px;margin:12px 0' }, [check, el('span', { class: 'tiny', text: '我已阅读并同意上述承诺' })]),
    el('div', { class: 'row', style: 'justify-content:flex-end;gap:8px' }, [cancel, accept]),
  ]);
  const bg = el('div', { class: 'modal-bg' }, [modal]);
  cancel.onclick = () => bg.remove();
  accept.onclick = () => { localStorage.setItem(COPYRIGHT_KEY, '1'); bg.remove(); onAccept(); };
  document.body.appendChild(bg);
}

